// src/components/StaticHero.jsx
import React, { useRef } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Stack,
  Paper,
  useTheme,
} from "@mui/material";
import PhoneInTalkRoundedIcon from "@mui/icons-material/PhoneInTalkRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import { motion, useScroll, useTransform } from "framer-motion";
import heroImg from "../assets/solar-hero.jpg";

const ACCENT = "#FF8A00";

export default function StaticHero() {
  const theme = useTheme();
  const sectionRef = useRef(null);

  /* ── Scroll hooks ── */
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"], // 0 cuando entra, 1 cuando sale
  });

  /* 1. Parallax fondo (0 → 60 px) */
  const bgY = useTransform(scrollYProgress, [0, 1], ["0px", "60px"]);

  /* 2. Contenido: 0 → −120 % (sale completo) y opacidad 1 → 0 */
  const contentY = useTransform(
    scrollYProgress,
    [0, 1],
    ["0%", "-120%"] // ← todo el bloque sube fuera
  );
  const contentOpacity = useTransform(scrollYProgress, [0, 0.4], [1, 0]);

  return (
    <Box
      id="contacto"
      component={motion.section}
      ref={sectionRef}
      style={{ backgroundPositionY: bgY }}
      sx={{
        position: "relative",
        minHeight: { xs: "100vh", md: "85vh" },
        background: `linear-gradient(0deg, rgba(0,0,0,.55), rgba(0,0,0,.55)), url(${heroImg}) center/cover no-repeat`,
        color: "#fff",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        px: { xs: 2, sm: 4, md: 8 },
        py: { xs: 8, md: 0 },
        alignItems: { md: "center" },
        gap: { xs: 6, md: 8 },
        overflow: "hidden", // oculta cuando sale por arriba
      }}
    >
      {/* —— Texto —— */}
      <Box
        component={motion.div}
        style={{ y: contentY, opacity: contentOpacity }}
        sx={{ flex: { md: "0 0 70%" }, maxWidth: { md: "70%" } }}
      >
        <Typography
          component="h2"
          sx={{
            fontFamily: "Ethos Nova, sans-serif",
            fontWeight: 800,
            lineHeight: 1.15,
            mb: 3,
            fontSize: { xs: "2.1rem", sm: "2.5rem", md: "3.4rem" },
          }}
        >
          ¿Listo para{" "}
          <Box component="span" sx={{ color: ACCENT }}>
            Cambiar a Energía Solar
          </Box>
          ?
        </Typography>

        <Typography variant="h6" sx={{ mb: 4, maxWidth: 640, opacity: 0.9 }}>
          Nuestro equipo local está listo para ayudarte a reducir tu recibo de
          luz. Llámanos o envíanos un mensaje: te asesoramos sin compromiso.
        </Typography>

        <Stack spacing={3}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <PhoneInTalkRoundedIcon sx={{ color: ACCENT }} />
            <Typography fontWeight={500}>662&nbsp;143&nbsp;0033</Typography>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <EmailRoundedIcon sx={{ color: ACCENT }} />
            <Typography fontWeight={500}>info@kwsolar.com</Typography>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <PlaceRoundedIcon sx={{ color: ACCENT, mt: "2px" }} />
            <Typography fontWeight={500}>
              Blvd Colosio C. Real del Arco,
              <br />
              Hermosillo, Sonora, México.
            </Typography>
          </Stack>
        </Stack>
      </Box>

      {/* —— Formulario —— */}
      <Box
        component={motion.div}
        style={{ y: contentY, opacity: contentOpacity }}
        sx={{ flex: { md: "0 0 30%" }, maxWidth: { md: "25%" } }}
      >
        <Paper
          elevation={4}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 4,
            bgcolor: "rgba(255,255,255,0.96)",
            backdropFilter: "blur(2px)",
            width: "100%",
          }}
        >
          <Stack spacing={3}>
            {[
              { label: "Nombre", type: "text" },
              { label: "Correo Electrónico", type: "email" },
              { label: "Teléfono", type: "tel" },
            ].map(({ label, type }) => (
              <TextField
                key={label}
                label={label}
                type={type}
                fullWidth
                variant="outlined"
                InputProps={{ sx: { bgcolor: "#fff", borderRadius: 2 } }}
              />
            ))}

            <TextField
              label="Mensaje"
              multiline
              rows={4}
              fullWidth
              variant="outlined"
              InputProps={{ sx: { bgcolor: "#fff", borderRadius: 2 } }}
            />

            <Button
              variant="contained"
              size="large"
              sx={{
                alignSelf: "flex-start",
                textTransform: "none",
                px: 6,
                py: 1.8,
                fontWeight: 700,
                borderRadius: "50px",
                bgcolor: ACCENT,
                "&:hover": { bgcolor: "#e07200" },
              }}
            >
              Enviar
            </Button>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}
